import { useState, useMemo, useEffect, useRef } from "react";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { Controller, useForm } from "react-hook-form";
import SearchInput from "@/components/SearchInput";
import usePagination from "@/hooks/usePagination";
import { useParams as useSearchParams, useSearchWithParams } from "@/hooks/useSearchParams";
import { ActivityTableRow } from "../types";
import { Path, useNavigate } from "@/router";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { getActivityTableColumns } from "../TableColumns";
import { useQuery } from "@apollo/client";
import { GET_ACTIVITIES } from "../../queries";
import MultiSelect from "@/components/MultiSelect";
import DateSelect from "@/components/DateSelect";
import { GET_SECTIONS } from "@/pages/billing/queries";
import { GET_MANAGEABLE_SECTIONS } from "../_form/queries";
import { reject } from "lodash";
import { isEmpty } from "lodash";
import { useParams, useOutletContext } from "react-router-dom";
import { ActivityTypeOutletContext } from "../_layout";
import { toUTCFormat } from "@/lib/dateHelpers";
import useActivityTypePermissions, { getCanAccessActivityManagement } from "@/hooks/useActivityTypePermissions";
import useCapabilities from "@/hooks/useCapabilities";
import { usePrimarySection } from "@/hooks/usePrimarySection";
import { Section } from "@/types";
import { showManagingSectionColumn } from "../../showManagingSectionColumn";

export default function ActivityTypeActivitiesPage() {
  const { activityTypeId = "" } = useParams();
  const { activityData } = useOutletContext<ActivityTypeOutletContext>();
  const activityTitle = activityData?.title ?? "";
  const showSectionFilter = showManagingSectionColumn(activityData?.sectionAvailability);
  const canAccessActivityManagement = useActivityTypePermissions(activityData || {});
  const capabilities = useCapabilities();
  const { primarySectionId } = usePrimarySection();
  const { params, addSearchParam, deleteSearchParams } = useSearchParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm",
    debounceMs: 300
  });
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const navigate = useNavigate();
  const { Menu, setMenu } = useTableRowMenu();

  const form = useForm({
    defaultValues: {
      dateRange: { startDate: "", endDate: "" }
    }
  });
  const { control, setValue } = form;

  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>(params.managingSectionIds || []);
  const [dateRange, setDateRange] = useState({
    startDate: params.startDate || "",
    endDate: params.endDate || ""
  });
  const hasAppliedSectionDefault = useRef(Boolean(params.managingSectionIds?.length));
  const { data: manageableSectionsData, loading: manageableSectionsLoading } = useQuery<{
    manageableSections: { nodes: Section[] };
  }>(GET_MANAGEABLE_SECTIONS, {
    variables: { input: { search: null } },
    skip: !showSectionFilter
  });
  const manageableSections = useMemo(
    () => manageableSectionsData?.manageableSections?.nodes ?? [],
    [manageableSectionsData]
  );

  useEffect(() => {
    if (!showSectionFilter) {
      return;
    }

    if (hasAppliedSectionDefault.current) {
      return;
    }

    if (params.managingSectionIds?.length) {
      hasAppliedSectionDefault.current = true;
      return;
    }

    if (manageableSectionsLoading) {
      return;
    }

    if (manageableSections.length === 1) {
      hasAppliedSectionDefault.current = true;
      const sectionId = manageableSections[0].id;
      setSelectedSectionIds([sectionId]);
      addSearchParam("managingSectionIds[]", sectionId);
      return;
    }

    hasAppliedSectionDefault.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSectionFilter, manageableSections, manageableSectionsLoading, params.managingSectionIds]);
  const activityFilters = useMemo(
    () => ({
      activityTypeId: activityTypeId,
      search: debouncedSearch || undefined,
      ...(dateRange.startDate && dateRange.endDate
        ? {
            dateRange: {
              from: dateRange.startDate,
              to: dateRange.endDate
            }
          }
        : {}),
      ...(showSectionFilter && selectedSectionIds.length > 0 ? { managingSectionIds: selectedSectionIds } : {})
    }),
    [activityTypeId, debouncedSearch, dateRange, selectedSectionIds, showSectionFilter]
  );

  const pendingSingleSectionDefault =
    showSectionFilter &&
    !params.managingSectionIds?.length &&
    (manageableSectionsLoading || (manageableSections.length === 1 && selectedSectionIds.length === 0));

  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityFilters]);

  const { data: activitiesData, loading: activitiesLoading } = useQuery<any>(GET_ACTIVITIES, {
    variables: { filters: activityFilters, ...paginationVariables },
    skip: pendingSingleSectionDefault
  });
  const { data: sectionsData } = useQuery(GET_SECTIONS, {
    variables: { input: {} },
    skip: !showSectionFilter
  });

  const tableData = useMemo(() => {
    return (
      activitiesData?.activities?.nodes?.map((activity: any) => ({
        ...activity,
        participants: activity.participations?.totalCount || 0,
        location: activity.event?.facility?.name || activity.event?.location,
        startDate: toUTCFormat(activity.allowParticipationStarting || activity.event?.startsAt),
        endDate: toUTCFormat(activity.allowParticipationThrough || activity.event?.endsAt),
        stage: activity.event?.status,
        section: activity.managingSection?.name,
        eventSlug: activity.event?.slug,
        eventName: activity.event?.title ? "Yes" : "No",
        managingSectionId: activity.managingSection?.id ?? null
      })) || []
    );
  }, [activitiesData]);

  const columns = useMemo(
    () =>
      getActivityTableColumns({
        navigate,
        activityTypeId,
        setMenu,
        createsEvent: activityData?.createsEvent,
        canAddParticipants: (row) =>
          getCanAccessActivityManagement(activityData || {}, capabilities, primarySectionId, row.managingSectionId),
        sectionAvailability: activityData?.sectionAvailability
      }),
    [navigate, setMenu, activityTypeId, activityData, capabilities, primarySectionId]
  );

  const filterState = {
    dateRange: !!(dateRange.startDate || dateRange.endDate),
    ...(showSectionFilter ? { managingSectionIds: selectedSectionIds.length > 0 } : {})
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const sectionOptions =
    sectionsData?.crmSections?.nodes.map((s: { id: string; name: string }) => ({
      value: s.id,
      label: s.name,
      displayText: s.name
    })) || [];

  const handleClearAllFilters = () => {
    clearSearch();
    setSelectedSectionIds([]);
    setDateRange({ startDate: "", endDate: "" });
    setValue("dateRange", { startDate: "", endDate: "" });
    deleteSearchParams(["managingSectionIds[]", "searchTerm", "startDate", "endDate"]);
    resetPagination();
  };
  const activitiesConnection = activitiesData?.activities;

  return (
    <>
      <Menu />
      <TableHeading
        heading={activityTitle + " Activities"}
        body={activityData?.description}
        actions={[
          {
            label: "Create Activity",
            icon: "add",
            variant: "filled",
            disabled: !canAccessActivityManagement,
            onClick: () => navigate(`/activities/activity-types/${activityTypeId}/create` as Path)
          }
        ]}
      />
      <Table<ActivityTableRow>
        data={tableData}
        loading={activitiesLoading}
        columns={columns}
        onClickRow={(row) => {
          const hasEvent = !!row.original.eventSlug;
          if (hasEvent) {
            navigate(`/activities/events/${row.original.eventSlug}/overview` as Path);
          } else {
            navigate(`/activities/activity-types/${activityTypeId}/activity/${row.original.id}` as Path, {
              state: { activity: row.original }
            });
          }
        }}
        data-testid="pat-activities-table"
        hasActiveFilters={activeFilterCount > 0 || !!debouncedSearch}
        renderFilteredEmptyState={() => (
          <EmptyState
            title="No Activities Found."
            caption={debouncedSearch ? "Try a different search" : ""}
            iconName="event"
          />
        )}
        renderNoDataEmptyState={() => <EmptyState title="No Activities Found." iconName="event" />}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(activitiesConnection, { loading: activitiesLoading })}
            onSortClick={onSortClick}
            useExpandedFilters={true}
            expandedFiltersState={filterState}
            onClearAllFilters={handleClearAllFilters}
            activeFilters={activeFilterCount}
          >
            <SearchInput
              placeholder="Search Activities"
              query={searchValue}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
              onChange={(e) => {
                setSearchValue(e);
                resetPagination();
              }}
            />
            {showSectionFilter && (
              <div data-name="managingSectionIds" data-testid="activity-section-filter">
                <MultiSelect
                  label="Section"
                  selectedValues={selectedSectionIds}
                  options={sectionOptions}
                  onSelect={(value, checked) => {
                    const updatedIds = checked
                      ? [...selectedSectionIds, value]
                      : selectedSectionIds.filter((v) => v !== value);

                    const filteredIds = reject(updatedIds, isEmpty);
                    setSelectedSectionIds(filteredIds);
                    if (filteredIds.length > 0) {
                      addSearchParam("managingSectionIds[]", filteredIds.join(","));
                    } else {
                      deleteSearchParams("managingSectionIds[]");
                    }
                  }}
                  onClear={() => {
                    setSelectedSectionIds([]);
                    deleteSearchParams("managingSectionIds[]");
                  }}
                  className="w-full"
                />
              </div>
            )}

            <div data-name="dateRange" data-filter-size="custom">
              <Controller
                name="dateRange"
                control={control}
                render={() => (
                  <DateSelect
                    key={`dateRange-${dateRange.startDate}-${dateRange.endDate}`}
                    label="Activity Date Range"
                    value={dateRange}
                    useModal={true}
                    placeholder="Show All"
                    onChange={(date: any) => {
                      if (date?.startDate && date?.endDate) {
                        setDateRange({ startDate: date.startDate, endDate: date.endDate });
                        addSearchParam("startDate", date.startDate);
                        addSearchParam("endDate", date.endDate);
                        resetPagination();
                      } else {
                        setDateRange({ startDate: "", endDate: "" });
                        deleteSearchParams(["startDate", "endDate"]);
                        resetPagination();
                      }
                    }}
                  />
                )}
              />
            </div>
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(activitiesConnection, { loading: activitiesLoading })}
            totalRowCount={activitiesConnection?.totalCount}
          />
        )}
      />
    </>
  );
}
